import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';
import Card from 'react-bootstrap/Card';
import CreateCategoryModal from '../components/CreateCategoryModal';
import CategoryTreeView from '../components/CategoryTreeView';

const AdminCategoryManagementPage = () => {
    const [categoryTree, setCategoryTree] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createMode, setCreateMode] = useState('parent'); // 'parent' or 'child'
    const [selectedParentId, setSelectedParentId] = useState(null);
    const { encryptedApiCall, API_BASE_URL, isAdmin, logout } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!isAdmin) {
            navigate('/categories');
            return;
        }
        fetchCategoryTree();
    }, [isAdmin]);

    const fetchCategoryTree = async () => {
        try {
            setLoading(true);
            const data = await encryptedApiCall(`${API_BASE_URL}/categories?tree=true`, {
                method: 'GET'
            });
            setCategoryTree(data);
        } catch (err) {
            if (err.message && err.message.includes('401')) {
                logout();
                navigate('/login');
                return;
            }
            setError(err.message || 'Failed to fetch categories');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateParent = () => {
        setCreateMode('parent');
        setSelectedParentId(null);
        setShowCreateModal(true);
    };

    const handleCreateChild = (parentId) => {
        setCreateMode('child');
        setSelectedParentId(parentId);
        setShowCreateModal(true);
    };

    const handleCategoryCreated = () => {
        setShowCreateModal(false);
        fetchCategoryTree(); // Refresh tree
    };

    const handleDeleteCategory = async (categoryId, isParent) => {
        // Build warning message based on category type
        let warningMessage;
        if (isParent) {
            warningMessage = `⚠️ WARNING: This will permanently delete:\n\n` +
                `• This parent category\n` +
                `• ALL child categories under it\n` +
                `• ALL tests in those categories\n` +
                `• ALL questions in those tests\n\n` +
                `This action is IRREVERSIBLE!\n\n` +
                `Are you absolutely sure you want to proceed?`;
        } else {
            warningMessage = `⚠️ WARNING: This will permanently delete:\n\n` +
                `• This sub category\n` +
                `• ALL tests in this category\n` +
                `• ALL questions in those tests\n\n` +
                `This action is IRREVERSIBLE!\n\n` +
                `Are you absolutely sure you want to proceed?`;
        }

        if (!window.confirm(warningMessage)) {
            return;
        }

        try {
            setError('');
            const result = await encryptedApiCall(
                `${API_BASE_URL}/categories/${categoryId}`,
                { method: 'DELETE' }
            );
            
            // Show success message
            alert(`✅ ${result.message || 'Category deleted successfully'}`);
            
            fetchCategoryTree(); // Refresh tree
        } catch (err) {
            setError(err.message || 'Failed to delete category. Please try again.');
        }
    };

    if (loading) {
        return (
            <Container className="text-center mt-5">
                <Spinner animation="border" />
            </Container>
        );
    }

    return (
        <Container className="mt-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <Button 
                        variant="outline-secondary" 
                        onClick={() => navigate('/categories')} 
                        className="me-2"
                    >
                        ← Back to Categories
                    </Button>
                    <h2 className="d-inline ms-3">Category Management</h2>
                </div>
                <div>
                    <Button 
                        variant="success" 
                        onClick={handleCreateParent}
                        className="me-2"
                    >
                        + Create Parent Category
                    </Button>
                </div>
            </div>

            {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

            <Card>
                <Card.Header>
                    <h5>Category Tree</h5>
                </Card.Header>
                <Card.Body>
                    {categoryTree.length === 0 ? (
                        <Alert variant="info">No categories found. Create a parent category to get started.</Alert>
                    ) : (
                        <CategoryTreeView
                            categories={categoryTree}
                            onCreateChild={handleCreateChild}
                            onDeleteCategory={handleDeleteCategory}
                        />
                    )}
                </Card.Body>
            </Card>

            <CreateCategoryModal
                show={showCreateModal}
                onHide={() => setShowCreateModal(false)}
                mode={createMode}
                parentId={selectedParentId}
                onCategoryCreated={handleCategoryCreated}
            />
        </Container>
    );
};

export default AdminCategoryManagementPage;

